// Pesi, soglie e parametri del Driver Performance Score.
//
// Vivono in un modulo a sé, e non dentro il calcolo, per una ragione precisa:
// il punteggio è collegato a premi e provvedimenti, quindi un punteggio erogato
// a marzo deve restare ricalcolabile a novembre **esattamente com'era**. Ogni
// snapshot persiste la `version` con cui è stato prodotto; cambiare i pesi
// crea una versione nuova e **non** riscrive la storia.
//
// Nessun import di `i18n` qui dentro: questo file viene copiato nel repo
// Functions insieme al modulo di calcolo. Le etichette stanno in
// `driverScoreLabels.js`.
//
// Contratto delle regole: `docs/architecture/driver-performance.md` (DPS-n).
//
// > Alzare `version` è obbligatorio a ogni modifica di questo file. Se due
// > punteggi diversi possono uscire dalla stessa versione, la ricostruibilità
// > storica è persa e non c'è modo di accorgersene a posteriori.

/** Chiavi dei sei componenti. Stabili: finiscono negli snapshot. */
export const SCORE_COMPONENTS = {
  /** Scarto fra pianificato ed esecuzione reale. */
  PUNCTUALITY: 'punctuality',
  /** Disponibilità: giornate di servizio sul totale delle giornate impegnate. */
  RELIABILITY: 'reliability',
  /** Procedure: pratiche di processo portate a termine. */
  COMPLIANCE: 'compliance',
  /** Attività svolte: pulizia, ricariche, manutenzioni — le cose fatte. */
  ACTIVITY: 'activity',
  /** Eventi negativi: multe, sinistri, avarie, reclami, richiami. */
  INCIDENTS: 'incidents',
  /** Stile di guida: eventi di condotta rilevati dalla telematica. */
  DRIVING_STYLE: 'drivingStyle',
};

/**
 * DPS-2 — provenienza di un campione temporale. Non è una scala di qualità
 * dell'autista: è quanto ci possiamo fidare del timestamp.
 */
export const PROVENANCE = {
  /** `actual_*` scritti dal job GPS `OrdersProgress`. */
  GPS: 'gps',
  /** Voce dell'app autista **con** posizione entro raggio dal punto atteso. */
  SELF_VERIFIED: 'self+',
  /** Voce dell'app senza posizione, o con posizione fuori raggio. */
  SELF_UNVERIFIED: 'self-',
  /** Chiusura da operatore: è quando ha cliccato lui, non quando è arrivato l'autista. */
  MANUAL: 'manual',
  /**
   * History non disponibile (spogliata dal listener ordini prima di arrivare a
   * Redux) e sul documento c'è solo `actual_*`, che a quel punto **può** essere
   * un valore derivato dalla history invece che dal GPS. Non si può
   * distinguere, quindi non si finge di poterlo fare.
   */
  UNKNOWN: 'unknown',
};

/**
 * DPS-13 — ruolo di un tipo di pratica nel punteggio, dichiarato sul documento
 * `process_types` nel campo `score_role`.
 *
 * Non si cablano slug: `process-effects.md` è esplicito sul fatto che una
 * conseguenza nuova di un workflow **si dichiara sul tipo**, non si aggiunge
 * come `if` in un hook. Un tipo senza `score_role` non entra nel punteggio, che
 * è il default giusto: un workflow nuovo non deve iniziare a pesare sulla
 * retribuzione di qualcuno perché qualcuno l'ha creato senza pensarci.
 */
export const PROCESS_SCORE_ROLE = {
  /** Adempimento dovuto: conta quanto viene portato a termine. */
  PROCEDURE: 'procedure',
  /** Attività svolta: pulizia, ricarica, manutenzione. Conta quanto se ne fa. */
  ACTIVITY: 'activity',
};

// ---------------------------------------------------------------------------
// Vocabolari e instradamento delle segnalazioni (DPS-10)
//
// Stanno qui e non in `driverPerformance/warnings.js` per la stessa ragione dei
// pesi: sono decisioni di **punteggio**, non logica di normalizzazione. Chi
// deve capire perché una categoria pesa (o non pesa) apre questo file, non
// cerca la tabella dentro l'algoritmo che la usa.
//
// Non sono numeri, quindi restano export a sé invece di finire dentro
// `DRIVER_SCORE_WEIGHTS`: chiamare "peso" un instradamento sarebbe comodo e
// falso.
// ---------------------------------------------------------------------------

/**
 * Vocabolario dell'app autisti → categoria canonica.
 * Le chiavi sono i valori di `_kCategories` in
 * `mbility-drivers/lib/screens/drivers_warnings.dart`. Se là ne compare una
 * nuova e qui non c'è, finisce in `altro` e non scora: un typo o una categoria
 * sconosciuta non deve poter inventare una penalità.
 */
export const APP_CATEGORY_MAP = {
  assenza: 'assenza',
  assenza_ingiustificata: 'assenza_ingiustificata',
  permesso: 'permesso',
  ritardo: 'ritardo',
  incidente: 'sinistro',
  avaria: 'veicolo',
  comportamento: 'comportamento',
  multa: 'multa',
  cliente: 'reclamo',
  altro: 'altro',
};

/** Categorie che il gestionale scrive in `type` (registro di `warningsLogic`). */
export const BACKOFFICE_CATEGORIES = [
  'ritardo',
  'comportamento',
  'sinistro',
  'documenti',
  'veicolo',
  'reclamo',
  'richiamo_verbale',
  'richiamo_scritto',
  'sospensione',
  'altro',
];

/**
 * Categoria → componente su cui pesa. Tutto il negativo confluisce in
 * `incidents`: multe, sinistri, avarie del mezzo, reclami, richiami e
 * sospensioni. Quello che NON è qui dentro compare nel fascicolo come contesto
 * e non toglie punti. Tre assenze deliberate:
 *
 * - `ritardo`: la puntualità si misura dai timestamp (componente `punctuality`).
 *   Farla pesare anche come segnalazione conterebbe lo stesso ritardo due volte.
 * - `assenza_ingiustificata`: entra in `reliability` come **giornata** nel
 *   denominatore della disponibilità (DPS-14), non come segnalazione. Contarla
 *   in tutti e due i posti sarebbe doppio conto.
 * - `assenza` / `permesso`: assenze comunicate, cioè il funzionamento normale di
 *   un rapporto di lavoro; pesano solo come giornate in `reliability`.
 *
 * `multa` invece **pesa**: la segnalazione dall'app e il verbale nel dominio
 * `violations` sono due record distinti, e la deduplicazione la fa il fatto che
 * il verbale conta solo con `responsibility: 'driver'` mentre la segnalazione
 * dall'app conta solo se `verified`. Se un giorno si vedessero doppioni, il
 * posto dove risolverli è qui, non nei pesi.
 */
export const WARNING_CATEGORY_COMPONENT = {
  sinistro: SCORE_COMPONENTS.INCIDENTS,
  veicolo: SCORE_COMPONENTS.INCIDENTS,
  comportamento: SCORE_COMPONENTS.INCIDENTS,
  reclamo: SCORE_COMPONENTS.INCIDENTS,
  multa: SCORE_COMPONENTS.INCIDENTS,
  richiamo_verbale: SCORE_COMPONENTS.INCIDENTS,
  richiamo_scritto: SCORE_COMPONENTS.INCIDENTS,
  sospensione: SCORE_COMPONENTS.INCIDENTS,
};

export const DRIVER_SCORE_WEIGHTS = {
  version: '3.0.0',

  /** Peso di ciascun componente. La somma non deve fare per forza 1: si normalizza. */
  components: {
    // v3.0.0 — ribilanciati per fare posto a `drivingStyle` restando a 100.
    // `incidents` resta a 20 di proposito: abbassarlo avrebbe annullato in
    // parte l'inasprimento delle penalità deciso nella 2.1.0. Il peso arriva
    // dai due componenti che oggi hanno copertura quasi nulla.
    [SCORE_COMPONENTS.PUNCTUALITY]: 25,
    [SCORE_COMPONENTS.DRIVING_STYLE]: 20,
    [SCORE_COMPONENTS.RELIABILITY]: 20,
    [SCORE_COMPONENTS.INCIDENTS]: 20,
    [SCORE_COMPONENTS.COMPLIANCE]: 8,
    [SCORE_COMPONENTS.ACTIVITY]: 7,
  },

  /**
   * DPS-12 — componenti spenti perché la procedura che li alimenta non è ancora
   * in vigore. Escono dal calcolo come non misurati, ma la UI lo dice con parole
   * diverse: non è "mancano i dati", è "non c'è ancora niente da misurare".
   *
   * Vuoto: `compliance` non passa più da `daily_checks` (che verrà rifatto) ma
   * dalle pratiche, e lì la copertura naturale fa già il suo lavoro — senza
   * pratiche di ruolo `procedure` il componente esce da sé.
   */
  disabledComponents: [],

  /**
   * DPS-1 — copertura minima perché un componente entri nel punteggio. Ereditata
   * da OCC-7, che sotto il 30% dichiara la puntualità rumore e mostra `—`.
   */
  minCoverage: 0.3,

  /** DPS-1 — sotto questo numero di componenti misurati il punteggio è `null`. */
  minMeasuredComponents: 2,

  /**
   * DPS-11 — i componenti che costituiscono una **misura positiva**: qualcuno ha
   * osservato come l'autista ha lavorato, e il risultato è un numero che può
   * uscire alto o basso. `incidents` è l'unico a detrazione: parte da 100 e
   * scende solo se esiste un evento a carico, quindi da solo direbbe "non
   * risulta nulla" — un'affermazione sull'archivio, non sulla persona.
   *
   * **Senza almeno uno di questi, il punteggio è `null`.**
   */
  evidenceComponents: [
    SCORE_COMPONENTS.PUNCTUALITY,
    SCORE_COMPONENTS.DRIVING_STYLE,
    SCORE_COMPONENTS.RELIABILITY,
    SCORE_COMPONENTS.COMPLIANCE,
    SCORE_COMPONENTS.ACTIVITY,
  ],

  /** DPS-2 — quanto vale un campione secondo la sua provenienza. */
  provenance: {
    [PROVENANCE.GPS]: 1,
    [PROVENANCE.SELF_VERIFIED]: 1,
    [PROVENANCE.SELF_UNVERIFIED]: 0.5,
    // Escluso: non entra nemmeno nel denominatore della copertura.
    [PROVENANCE.MANUAL]: 0,
    [PROVENANCE.UNKNOWN]: 0.5,
  },

  /**
   * DPS-2 — raggio entro cui una posizione conferma il campione. Generoso di
   * proposito: l'app prova prima `getLastKnownPosition()`, che può essere
   * vecchia, e l'`accuracy` non viene salvata nella voce di history. Serve a
   * **promuovere** un campione, mai da sola a produrre una penalità.
   */
  geoRadiusM: 500,

  /**
   * DPS-3 — inasprita nella v2.1.0: tolleranza dimezzata (10' → 5') e curva
   * verso lo zero più ripida (30' oltre tolleranza → 20'). Un ritardo di 25'
   * — prima valeva 50 punti — ora vale 0. Scelta esplicita del committente:
   * "più severi sulla puntualità".
   */
  punctuality: {
    /** DPS-3 — sotto questa soglia il ritardo non è un evento. */
    toleranceMin: 5,
    /** Ritardo, oltre la tolleranza, che porta il sotto-punteggio a 0. */
    zeroAtMin: 20,
  },

  /**
   * DPS-19 — stile di guida, dagli eventi di condotta SafeFleet.
   *
   *   tasso = Σ(eventi × peso) / (km / 100)      // eventi pesati per 100 km
   *
   * Stessa curva della puntualità: sotto `tolerancePer100km` è 100 pieno, poi
   * discesa lineare fino a 0 a `zeroAtPer100km` oltre la tolleranza. Una sola
   * forma di scala in tutto il modulo, più facile da spiegare a chi la subisce.
   *
   * > Le tre soglie sono **decise a tavolino**: nessuno ha ancora guardato la
   * > distribuzione reale. Sono il primo parametro da rivedere dopo il primo
   * > periodo di esercizio, insieme ad `activityTargetPerDay`.
   */
  drivingStyle: {
    /** Sotto questo ritmo di eventi pesati per 100 km non c'è penalità. */
    tolerancePer100km: 3,
    /** Ritmo, oltre la tolleranza, che porta il sotto-punteggio a 0. */
    zeroAtPer100km: 12,
    /**
     * Sotto questi km il componente **non è misurato** (DPS-1): su una manciata
     * di chilometri due frenate brusche darebbero un tasso enorme e un giudizio
     * che il dato non regge.
     */
    minKm: 100,
  },

  /**
   * DPS-19 — quanto pesa ciascun tipo di evento SafeFleet.
   * Le chiavi sono i `type` realmente emessi dai dispositivi della flotta,
   * letti dall'API di produzione (non dalla documentazione, che non li elenca).
   *
   * Un tipo assente pesa **0**: se SafeFleet ne introduce uno nuovo, non può
   * iniziare a togliere punti finché qualcuno non decide quanto vale.
   */
  safefleetEventWeights: {
    /**
     * Urto rilevato dall'accelerometro. Vale otto frenate brusche: non è una
     * guida spigolosa, è un impatto. Resta però **in questo componente** e non
     * fra gli eventi negativi — quelli raccolgono ciò che qualcuno dichiara,
     * questo è ciò che un sensore rileva, e mescolare le due cose indebolirebbe
     * proprio la distinzione di provenienza che DPS-2 difende.
     *
     * Su 12 mesi ne è stato trovato **uno solo**: se risultassero frequenti e
     * banali (un dosso preso male genera lo stesso evento di un tamponamento),
     * il peso va abbassato — non il componente ripensato.
     */
    impact: 8,
    harsh_brake: 1,
    harsh_acceleration: 1,
    /**
     * Di gran lunga il più frequente — su un mezzo, 952 curve contro 299
     * frenate in un anno. A peso pieno deciderebbe da solo il punteggio di
     * tutti, coprendo il segnale degli altri due.
     */
    harsh_cornering: 0.5,
    // `idle`, `no_gps_signal`, `geofence_entered`, `geofence_left` non compaiono
    // di proposito: sosta, guasto del dispositivo e passaggi di geofence non
    // dicono niente su come si guida.
  },

  /**
   * DPS-14 — disponibilità. `Presenza` è un **tasso**, non una somma di
   * penalità: un'assenza dura giorni, e una detrazione fissa per occorrenza
   * azzererebbe il componente a chiunque prenda una settimana di ferie.
   *
   *   Presenza = 100 × giornate di servizio / (giornate di servizio + assenze pesate)
   *
   * Il peso per tipo è il **solo** punto da toccare per cambiare quanto conta
   * un'assenza. Oggi valgono tutte 1 — scelta esplicita del committente: ferie e
   * malattia pesano come l'assenza ingiustificata.
   *
   * > ⚠️ `sick` e `vacation` a 1 significa che un autista in malattia lunga, o
   * > che gode delle ferie che l'azienda gli ha assegnato, perde punteggio e
   * > quindi premio. È la parte del sistema più esposta a contestazione, perché
   * > tocca condizioni protette e non comportamenti. Portarli a 0 li rende
   * > visibili nel fascicolo senza farli pesare: è una riga, e non richiede di
   * > toccare il calcolo.
   */
  absenceWeights: {
    assenza_ingiustificata: 1,
    sick: 1,
    vacation: 1,
    permission: 1,
    unavailable: 1,
    // `pause` non compare: è dentro il turno, non è una giornata di assenza.
  },

  /**
   * DPS-15 — attività. Tasso sulle giornate lavorate, con un tetto oltre il
   * quale è 100:
   *
   *   Attività = 100 × min(1, (attività chiuse / giornate lavorate) / target)
   *
   * `target` è il ritmo che vale il massimo.
   *
   * v2.1.0: portato da 0.2 (un'attività ogni cinque giornate) a 1.5, su
   * indicazione esplicita del committente — le attività base (pulizia,
   * ricarica) attese sono "una o due al giorno", non una ogni cinque. 1.5 è
   * il punto medio fra "almeno una" e "due": un autista che ne fa una sola al
   * giorno resta sotto il massimo (100 × 1/1.5 ≈ 67), due lo raggiungono e lo
   * superano restando a 100.
   *
   * > Resta **deciso a tavolino**: finché non si guarda la distribuzione reale
   * > su qualche mese, non c'è modo di sapere se 1.5 è il punto giusto. È il
   * > primo parametro da rivedere dopo il primo periodo di esercizio.
   */
  activityTargetPerDay: 1.5,

  /**
   * DPS-15 — quanto vale ciascun tipo di evento come **attività svolta**.
   * Le chiavi sono i tipi di `EVENT_TYPES` (`modules/logic/eventsLogic.js`).
   *
   * Prima era un flag booleano `driver_activity` sul registro dei tipi: contava
   * o non contava. Con un peso la domanda "una pulizia vale quanto una
   * manutenzione?" diventa una decisione visibile invece di una conseguenza
   * implicita. Oggi valgono tutte **1**, che è esattamente il comportamento
   * precedente — chi vorrà differenziarle tocca solo questo blocco.
   *
   * Un tipo assente pesa **0**. I tipi `system` non entrano mai comunque
   * (sono specchi di record di altre collection: doppio conto con la fonte).
   */
  activityEventWeights: {
    vehicle_cleaning: 1,
    vehicle_charge: 1,
    vehicle_maintenance: 1,
    flyering: 1,
  },

  /** DPS-6 — decadimento temporale degli eventi negativi. */
  decay: {
    /** Mesi a peso pieno. */
    fullMonths: 3,
    /** Mesi oltre i quali un evento non pesa più. */
    zeroMonths: 12,
  },

  /**
   * Punti detratti da 100 per evento negativo, dopo il decadimento. Le chiavi
   * sono le categorie normalizzate da DPS-10.
   *
   * Inasprite nella v2.1.0 (~30-35% in più, ordine relativo invariato): scelta
   * esplicita del committente, "più severi sugli eventi negativi". Con la
   * sospensione a 75, due sospensioni nello stesso trimestre bastano da sole
   * a spingere `incidents` sotto la fascia "Critico" (max(0, 100-150)=0).
   */
  penalties: {
    [SCORE_COMPONENTS.INCIDENTS]: {
      sospensione: 75,
      richiamo_scritto: 40,
      sinistro: 35,
      comportamento: 20,
      reclamo: 20,
      richiamo_verbale: 20,
      multa: 15,
      veicolo: 15,
    },
  },

  /**
   * Fasce di lettura. Il numero secco resta il calcolo; in UI si mostra prima la
   * fascia, perché due cifre suggeriscono una precisione che non abbiamo.
   */
  bands: [
    { min: 85, key: 'excellent', label: 'Ottimo', variant: 'success' },
    { min: 70, key: 'good', label: 'Buono', variant: 'teal' },
    { min: 50, key: 'fair', label: 'Da monitorare', variant: 'warning' },
    { min: 0, key: 'poor', label: 'Critico', variant: 'danger' },
  ],
};

/** La fascia di un punteggio. `null` (non valutabile) non ha fascia. */
export function scoreBand(score, config = DRIVER_SCORE_WEIGHTS) {
  if (score == null || !Number.isFinite(score)) return null;
  return config.bands.find((b) => score >= b.min) || config.bands[config.bands.length - 1];
}
